/* global Highcharts:false */
/* eslint-disable */

import { get, range } from 'lodash';
import moment from 'moment';
import Vue from 'vue'
import store from '@/store';
import { getLeadingTradedContract, largeNumberDisplay, roundToTwo, isHighChartsReady } from '@/utils/helpers';
import marketStateTooltip from '@/components/market/market-state-tooltip/market-state-tooltip.vue';
import marketContractImage from '@/components/market/market-contract-image/market-contract-image.vue';
import { COLOR_MAP, getColorForPalletteCandidate } from '@/components/pages/page-election-map-new/election-map/election-map.vue';
import { TAB_SEVEN_DAYS, TAB_NINETY_DAYS } from '@/components/market/market-detail-charts/timeline/timeline.vue';
import constants from '@/constants';
import Contract from '@/models/contract';

const DATE_MONTHLY_FORMAT = 'DD MMM';
const DATE_YEARLY_FORMAT = 'DD MMM YYYY'
const CONTRACT_BAR_CHART_GRADIENT = '#c6ebf2';
const CONTRACT_BAR_CHART_GRADIENT_STOCK = '#c6ebf2';
const CONTRACT_LINE_CHART_MARKER_FILL_COLOR = '#fff';
const CONTRACT_SELECTION_COLOR = '#c97575';
const CONTRACT_SELECTION_COLOR_ALT = '#fff';


const MarketStateTooltipComponent = Vue.extend(marketStateTooltip);
const MarketContractImageComponent = Vue.extend(marketContractImage);

function splitArray(arr, size) {
  const result = [];
  const noOfChunks = Math.ceil(arr.length / size);
  for (let i = 0; i < noOfChunks; i += 1) {
    result.push(arr.slice(i * size, (i + 1) * size));
  }
  return result;
}

function volumeRoundUp(num, base) {
	return (num % base !== 0) ? num - num % base + base : num;
}

function volumeGetBase(num) {
  if (num >= 10000) return 10000
  if (num >= 5000) return 5000
  if (num >= 2500) return 2500
  if (num >= 1000) return 1000
	if (num >= 500) return 500
  if (num >= 250) return 250
  if (num >= 100) return 100
  if (num >= 10) return 10
  return 1
}

function volumeNormalize(num, base, parts) {
  let result = num
  if (result % parts === 0) return result
  do {
    result = result + base
  } while (result % parts !== 0)
  return result
}

function volumeGetRange(num, parts = 5) {
	const base = volumeGetBase(num);
	const max = volumeRoundUp(num, base);
  const normalizedMax = volumeNormalize(max, base, parts)
  const diff = normalizedMax / parts
	return range(0, normalizedMax + diff, diff);
}

function getTradeVolumeTickPositions({ data, parts = 5, show = false }) {
  const defaults = [ 0, 2, 4, 6, 8, 10 ];
  if (!show) {
    return defaults
  }
  const max = Math.max.apply(null, data);
  if (!max) {
    return defaults;
  }
  return volumeGetRange(max, parts);
}

if (isHighChartsReady()) {
  Highcharts.SVGRenderer.prototype.symbols.download = (x, y, w, h) => {
    return [
      // Arrow stem
      'M', x + w * 0.5, y,
      'L', x + w * 0.5, y + h * 0.7,
      // Arrow head
      'M', x + w * 0.3, y + h * 0.5,
      'L', x + w * 0.5, y + h * 0.7,
      'L', x + w * 0.7, y + h * 0.5,
      // Box
      'M', x, y + h * 0.9,
      'L', x, y + h,
      'L', x + w, y + h,
      'L', x + w, y + h * 0.9,
    ];
  };
}


function getExporting() {
  if (store.getters['app/isLayoutMobile']) {
    return {
      enabled: false,
    };
  }
  return {
    enabled: false,
  };
}

export class ContractChartGroupStock {
  constructor({ market, timeline, data, chartOptions = {}, prepare = true }) {
    this.market = market;
    this.contracts = market.contracts;
    this.timeline = timeline;
    this.data = data;
    this.contractXaxis = [];
    this.volume = [];
    this.stock = [];
    this.lastXaxisLabelIndex = 0;
    this.contractXaxis = [];
    this.chartOptions = chartOptions;
    if (prepare) this.prepareChartGroupData();
  }
  static renderTimeout() {
    return 300;
  }
  isTimelineDaily() {
    return this.timeline.includes('h');
  }
  prepareDateString(contractData, index, total) {
    let label;
    if (this.isTimelineDaily()) {
      const value = -(total - index);
      label = `${value}h`;
    } else {
      label = moment(contractData.date).format(DATE_MONTHLY_FORMAT);
    }
    this.contractXaxis[index] = label;
  }
  prepareChartGroupData() {
    this.data.forEach((item, index) => {
      this.prepareDateString(item, index, this.data.length);
      this.prepareSeries(index, item);
    });
  }
  getCandleStickChartData() {
    const name = (this.data.length > 0) ? this.data[0].contractName : '';
    const that = this;
    return {
      id: 'candle',
      type: 'candlestick',
      showInLegend: false,
      data: this.stock,
      name,
      yAxis: 0,
      tooltip: {
        valueSuffix: '¢',
        pointFormatter: function volumeTooltipFormatter(i) {
          const point = {
            open: this.open,
            low: this.low,
            high: this.high,
            close: this.close,
            date: that.contractXaxis[this.index]
          }
          return `<small>${point.date}</small><br><b>Open</b>: ${point.open}¢<br/><b>High</b>: ${point.high}¢<br/><b>Low</b>: ${point.low}¢<br/><b>Close</b>: ${point.close}¢`
        },
      },
    }
  }
  getVolumeChartData() {
    const that = this;
    return {
      id: 'volume',
      yAxis: 1,
      color: {
        linearGradient: [0, 0, 0, 92],
        stops: [
            [0, CONTRACT_BAR_CHART_GRADIENT_STOCK],
            [1, CONTRACT_BAR_CHART_GRADIENT_STOCK],
        ],
      },
      data: this.volume,
      showInLegend: false,
      name: 'Total Volume',
      borderRadius: 2,
      type: 'column',
      tooltip: {
        pointFormatter: function volumeTooltipFormatter() {
          return `<small>${that.contractXaxis[this.index]}</small><br>Total Volume: <strong>${largeNumberDisplay(this.y, false)}</strong><br>`;
        },
      },
    };
  }
  prepareSeries(index, item) {
    const timestamp = +moment(item.date);
    this.volume[index] = [
      timestamp,
      Number((item.tradeVolume || 0).toFixed(1))
    ];
    this.stock[index] = [
      timestamp,
      Number((100 * (item.openSharePrice || 0)).toFixed(2)),
      Number((100 * (item.highSharePrice || 0)).toFixed(2)),
      Number((100 * (item.lowSharePrice || 0)).toFixed(2)),
      Number((100 * (item.closeSharePrice || 0)).toFixed(2))
    ];
  }
  getXaxisLabel () {
    const label = this.contractXaxis[this.lastXaxisLabelIndex]
    this.lastXaxisLabelIndex += 1;
    return label;
  }
  getOptions() {
    const showVolume = get(this.chartOptions, 'showVolume', false);
    const volume = this.getVolumeChartData();
    const series = [];
    const upColor = (this.data.length > 0) ? this.data[0].lineColor : '#07a0bb';

    series.push(this.getCandleStickChartData());

    if (showVolume) {
      series.push(volume);
    }

    let max = undefined;
    let tickPositions = undefined;
    const values = [];

    if (series.length) {
      series[0].data.forEach((i) => {
        values.push(i[1]);
      });
    }
    if (values.length && values.every(v => v === values[0])) {
      max = 100;
      tickPositions = [0, 20, 40, 60, 80, 100, 120];
    }

    return {
      navigator: {
          enabled: false
      },
      scrollbar: {
        enabled: false,
      },
      rangeSelector: {
          enabled: false,
      },
      legend: {
        enabled: false,
      },
      title: {
          text: ''
      },
      credits: {
        enabled: false,
      },
      xAxis: [{
        tickLength: 0,
        lineWidth: 0,
        labels: {
          formatter: function xAxisLabelFormatter() {
            return '';
          },
        },
      }],
      yAxis: [{
          height: showVolume ? '55%' : '100%',
          max,
          tickPositions,
          allowDecimals: false,
          labels: {
            formatter: function yAxisLabelFormatter() {
              if (this.value >= 0) {
                return `${this.value}¢`;
              }
            },
          },
          title: {
              text: ''
          },
          resize: {
              enabled: true
          },
        opposite: true,
      }, {
          visible: showVolume,
          top: '60%',
          height: '40%',
          offset: 0,
          tickPositions: getTradeVolumeTickPositions({
            data: volume.data.map((i) => i[1]),
            parts: 2,
            show: showVolume
          }),
          allowDecimals: false,
          labels: {
            formatter: function yAxisOppositeLabelFormatter() {
              if (this.value >= 0) {
                return largeNumberDisplay(this.value, false);
              }
            },
          },
          title: {
              text: ''
          },
        opposite: false,
      }],
      plotOptions: {
        candlestick: {
            color: '#fff',
            upColor: upColor
        }
      },
      tooltip: {
        split: true,
        shared: false,
        headerFormat: ''
      },
      series,
      exporting: {
        enabled: false
      }
    };
  }
  draw(container) {
    const options = this.getOptions();
    return Highcharts.stockChart(container, options);
  }
}

export class ContractChartGroup {
  constructor({ market, timeline, data, chartOptions = {}, prepare = true }) {
    this.market = market;
    this.contracts = market.contracts;
    this.timeline = timeline;
    this.data = data;
    this.contractSeries = {};
    this.contractSeriesNameId = {};
    this.volume = {};
    this.contractXaxis = [];
    this.priceLineType = 'line';
    this.volumeLineType = 'column';
    this.chartOptions = chartOptions;
    if (prepare) this.prepareChartGroupData();
  }
  static renderTimeout() {
    return 300;
  }
  getVolumeChartData() {
    return {
      yAxis: 1,
      color: {
        linearGradient: [0, 0, 0, 92],
        stops: [
            [0, CONTRACT_BAR_CHART_GRADIENT],
            [1, CONTRACT_BAR_CHART_GRADIENT],
        ],
      },
      data: Object.values(this.volume).map(el => Number(el.toFixed(1))),
      showInLegend: false,
      name: 'Total Volume',
      borderRadius: 2,
      type: this.volumeLineType,
      tooltip: {
        pointFormatter: function volumeTooltipFormatter() {
          return `Total Volume: <strong>${largeNumberDisplay(this.y, false)}</strong><br>`;
        },
      },
    };
  }
  isTimelineDaily() {
    return this.timeline.includes('h');
  }
  isMarkerEnabledForTimeline(seriesSize) {
    if (store.getters['app/isLayoutMobile']) {
      return this.timeline === TAB_SEVEN_DAYS;
    }
    return seriesSize <= 30;
  }
  prepareLabels(item) {
    if (!(item.contractId in this.contractSeries)) {
      const color = item.additional ? item.additionalLineColor : item.lineColor;
      this.contractSeriesNameId[item.contractName] = item.contractId;
      this.contractSeries[item.contractId] = {
        color,
        lineWidth: 2,
        marker: {
          enabled: false,
          symbol: 'circle',
          fillColor: CONTRACT_LINE_CHART_MARKER_FILL_COLOR,
          lineWidth: 1,
          lineColor: null,
          radius: 5,
        },
        data: [],
        yAxis: 0,
        showInLegend: false,
        name: item.contractName,
        contractId: item.contractId,
        type: this.priceLineType,
        tooltip: {
          valueSuffix: '¢',
        },
      };
    }
  }
  prepareSeries(index, item) {
    this.contractSeries[item.contractId].data.push(item.chartPrice);
    // volume is build as sum of contracts
    // on particular points in time
    if (!(index in this.volume)) {
      this.volume[index] = 0;
    }
    if (item.tradeVolume) {
      this.volume[index] += item.tradeVolume;
    }
  }
  prepareDateString(contractData, index) {
    if (this.isTimelineDaily()) {
      const value = -(index + 1);
      return `${value}h`;
    }
    const dt = moment(contractData.date);
    const dateYear = dt.year();
    const currentYear = moment().year();
    const format = dateYear === currentYear ? DATE_MONTHLY_FORMAT : DATE_YEARLY_FORMAT;
    return moment(contractData.date).format(format);
  }
  prepareChartGroupData() {
    let lastContractId = null;
    let lastContractIndex = 0;
    const dateStrings = new Set();

    this.data.forEach((item, index) => {
      if (index === 0 || item.contractId !== lastContractId) {
        lastContractIndex = 0;
      }
      dateStrings.add(this.prepareDateString(item, lastContractIndex));
      this.prepareLabels(item);
      this.prepareSeries(lastContractIndex, item);
      lastContractIndex += 1;
      lastContractId = item.contractId;
    });
    for (const [key, value] of Object.entries(this.contractSeries)) {
      this.contractSeries[key].marker.enabled = this.isMarkerEnabledForTimeline(this.contractSeries[key].data.length);
    }
    this.contractXaxis = Array.from(dateStrings);
    // we relate to 24h hour format so respacting prepareDateString method
    // we have to reverse
    if (this.isTimelineDaily()) {
      this.contractXaxis = this.contractXaxis.reverse();
    }
  }
  getOptions() {
    const showVolume = get(this.chartOptions, 'showVolume', false);
    const volume = this.getVolumeChartData();
    const series = [];
    const isMobile = store.getters['app/isLayoutMobile'];
    const totalContractSeries = Object.keys(this.contractSeries).length;

    if (showVolume) {
      series.push(volume);
    }

    Object.values(this.contractSeries).forEach((contractSeries) => {
      series.push(contractSeries);
    });

    let max = undefined;
    let tickPositions = undefined;
    let endOnTick = false;
    let maxes = [];
    const sliceIndex = showVolume ? 1 : 0;

    series.slice(sliceIndex).forEach((i) => {
      maxes.push(Math.max.apply(null, i.data));
    });
    const totalMax = Math.max.apply(null, maxes);

    if (totalMax < 80) {
      endOnTick = true;
    }
    if (totalContractSeries === 1 && series[sliceIndex].data.every(v => v === series[sliceIndex].data[0])) {
      max = 100;
      tickPositions = [0, 20, 40, 60, 80, 100];
    }


    return {
      title: {
        text: '',
      },
      subtitle: {
        text: '',
      },
      tooltip: {
        shared: true,
      },
      credits: {
        enabled: false,
      },
      yAxis: [
        {
          tickPositions,
          max,
          endOnTick,
          allowDecimals: false,
          title: { text: '' },
          gridLineWidth: 0,
          labels: {
            formatter: function yAxisLabelFormatter() {
              if (this.value >= 0) {
                return `${this.value}¢`;
              }
            },
          },
          opposite: true,
        },
        {
          allowDecimals: false,
          tickPositions: getTradeVolumeTickPositions({
            data: volume.data,
            parts: 5,
            show: showVolume
          }),
          title: {
            text: '',
          },
          gridLineWidth: 0,
          tickInterval: 5,
          labels: {
            style: {
              opacity: showVolume ? 1 : 0.15,
            },
            formatter: function yAxisOppositeLabelFormatter() {
              if (this.value >= 0) {
                return largeNumberDisplay(this.value, false);
              }
            },
          },
        },
      ],
      xAxis: [
        {
          categories: this.contractXaxis,
          crosshair: true,
          tickLength: 0,
          lineWidth: 0,
          tickPositioner: function () {
            const desktopBreak = 10;
            if (isMobile && this.categories && this.categories.length > 2) {
              return [
                0,
                Math.floor(this.categories.length / 2),
                this.categories.length - 1]
            }
            if (!isMobile && this.categories && this.categories.length > desktopBreak) {
              const length = this.categories.length
              const step = Math.ceil(length / desktopBreak)
              const positions = []
              for (let i = 0; i < length; i += step) {
                positions.push(i)
              }
              // Always include the last position
              if (positions[positions.length - 1] !== length - 1) {
                positions.push(length - 1);
              }
              return positions;
            }
            return this.tickPositions;
          },
        },
      ],
      series,
      plotOptions: {
        series: {
          cursor: 'pointer',
        },
        line: {
          allowPointSelect: false,
        },
        column: {
          allowPointSelect: false,
          pointPadding: 0,
          groupPadding: 0,
        },
      },
      exporting: getExporting.bind(this)(),
    };
  }
  draw(container) {
    const options = this.getOptions();
    return Highcharts.chart(container, options, (chart) => {
      const xAxis = chart.xAxis[0];
      const yAxis = chart.yAxis[0];

      if (get(this.chartOptions, 'showContractImagesOnChart', false)) {
          chart.series.forEach((s) => {
            if (s.type === 'line') {
              const lastPoint = s.data[s.data.length - 1];
              const lastX = xAxis.toPixels(lastPoint.x);
              const lastY = yAxis.toPixels(lastPoint.y);
              const contractId = this.contractSeriesNameId[lastPoint.series.name];
              const contract = this.market.contracts.find(i => i.contractId === contractId);
              if (contract) {
                const instance= new MarketContractImageComponent({
                  propsData: {
                    contract,
                    color: lastPoint.series.color,
                  },
                });
                instance.$mount();
                chart.renderer.text(instance.$el.outerHTML, lastX + 7, lastY - 5, true).add();
              }
            }
          });
      }
    });
  }
}


export class PriceChangeChart {
  constructor(contract, data) {
    this.contract = contract;
    this.data = data;
    this.colorPositive = '#7dc671';
    this.colorNegative = '#c63540';
    this.colorGrid = '#fff';
    this.lineWidth = 1;
    this.lineType = 'line';
  }
  static renderTimeout() {
    return 300;
  }
  getSeries() {
    const seriesData = this.data.map(el => 50 - ((el.closeSharePrice || 0.5) * 100));
    return [
      {
        data: seriesData,
        lineWidth: this.lineWidth,
        type: this.lineType,
        color: this.colorPositive,
        negativeColor: this.colorNegative,
        marker: { enabled: false, states: { hover: { enabled: false } } },
        states: { hover: { enabled: false } },
      },
    ];
  }
  getOptions() {
    return {
      chart: {
        margin: [0, 0, 0, 0],
        spacingTop: 0,
        spacingBottom: 0,
        spacingLeft: 0,
        spacingRight: 0,
      },
      plotOptions: {},
      legend: { enabled: false },
      credits: { enabled: false },
      tooltip: { enabled: false },
      series: this.getSeries(),
      title: { text: '', style: { display: 'none' }, margin: 0 },
      yAxis: { labels: { enabled: false }, gridLineColor: this.colorGrid },
      xAxis: { labels: { enabled: false }, gridLineColor: this.colorGrid },
      exporting: {
        enabled: false,
      },
    };
  }
  draw(container) {
    const options = this.getOptions();
    Highcharts.chart(container, options);
  }
}

export class ElectionMapChartOverall {
  constructor(data) {
    this.data = data;
  }
  draw(container) {
    const options = this.getOptions();
    Highcharts.mapChart(container, options);
  }
  static renderTimeout() {
    return 300;
  }
  getColorValue ({ marketData, stateCode, stateName, stateVotes }) {
    if (!marketData.contracts) return 0;
    const targetContract = getLeadingTradedContract(marketData.contracts);
    if (!targetContract || targetContract.lastTradePrice === 0.5)  return 0;
    return targetContract.name === 'Democratic' ? 1 : 2;
  }
  formatTooltip() {
    const index = this.point.index;
    const data = this.point.series.userOptions.data[index];
    const { marketData } = data;
    if (!marketData.contracts) return false;
    const instance= new MarketStateTooltipComponent({
      propsData: {
        ...data
      }
    });
    instance.$mount();
    return instance.$el.outerHTML;
  }
  formatDataLabel() {
    if (this.point.stateCode === 'HI') return '';
    const contracts = this.point.marketData.contracts || [];
    const targetContract = getLeadingTradedContract(contracts);
    const stateCode = this.point.properties['hc-a2'];
    const showStates = [
      'VT', 'NH', 'MA', 'RI', 'CT', 'NJ', 'DE', 'MD', 'DC'
    ];
    if (!targetContract) {
      if (contracts.length > 0) {
        return '-';
      }
      return showStates.includes(stateCode) ? stateCode : '';
    }
    const contract = new Contract(targetContract);
    return `${contract.lastTradePriceAsCents}¢`;
  }
  prepareSeries() {
    const mapData = Highcharts.geojson(Highcharts.maps['countries/us/custom/us-small']);
    const statesData = [];

    mapData.forEach((value, index) => {
      const path = value.geometry.coordinates;
      const copy = {
        path: path
      };

      if (path[0][1] === 9727) {
        Highcharts.seriesTypes.map.prototype.getBox.call({}, [copy]);
        value.middleX = ((path[0][1] + path[1][1]) / 2 - copy._minX) / (copy._maxX - copy._minX);
        value.middleY = ((path[0][2] + path[2][2]) / 2 - copy._minY) / (copy._maxY - copy._minY);
      }

      const stateCode = value.properties['hc-a2'];
      const stateName = value.name.toUpperCase();
      const marketData = this.data[stateCode] || {};
      value.stateName = stateName;
      const stateVotes = constants.statesCodeToElectoralVotesMap[stateCode]

      statesData[index] = {
        stateCode,
        stateName,
        stateVotes,
        marketData,
        value: this.getColorValue({ marketData, stateCode, stateName, stateVotes }),
      }
    });

    return { mapData, statesData }
  }
  getBaseOptions({ mapData, statesData }) {
    return {
      plotOptions : {
        series : {
          cursor : 'pointer',
          dataLabels: {
            style: {
              textOutline: 0,
            },
          },
        }
      },
      exporting: {
        enabled: false,
      },
      legend: {
        enabled: false,
      },
      credits: {
        enabled: false,
      },
      mapNavigation: {
          enabled: false,
          enableButtons: false
      },
      xAxis: {
          labels: {
              enabled: false
          }
      },
      title: {
        text: ''
      },
      chart: {
        margin: [0, 0, 0, 0],
        spacing: [0, 0, 0, 0],
      },
      colorAxis: {
        dataClasses: [,
          {
            from: 0,
            to: 0,
            color: '#c8c8c8',
            name: 'N/A'
          },
          {
            from: 1,
            to: 1,
            color: '#2f88dc',
            name: 'Clinton'
          },
          {
            from: 2,
            to: 2,
            color: '#bc1500',
            name: 'Trump'
          }
        ]
      },
      tooltip: {
        enabled: store.getters['app/isLayoutDesktop'],
        formatter: this.formatTooltip,
        hideDelay: 200,
        useHTML: true,
        borderRadius: 0,
        borderWidth: 0,
        backgroundColor: 'white',
        padding: 0,
        shadow: false,
        followPointer: true,
      },
      series: [{
          mapData: mapData,
          data: statesData,
          joinBy: 'stateName',
          name: 'States',
          events: {
            mouseOut: function(e) {
              store.dispatch('market/setMarketMapTooltipVisible', false);
            },
            click: function(e) {
              const marketId = get(e, 'point.marketData.marketId', null);
              if (marketId) {
                window.location.href = `https://www.predictit.org/markets/detail/${marketId}`;
                // router.push({
                //   name: 'market-details',
                //   params: {
                //     marketId,
                //   }
                // });
              }
            }
          },
          dataLabels: {
              enabled: true,
              formatter: this.formatDataLabel,
              style: {
                  fontSize: store.getters['app/isLayoutMobile'] ? '10px' : '12px',
                  color: 'white'
              }
          },
      }, {
        name: 'Separators',
        type: 'mapline',
        data: Highcharts.geojson(Highcharts.maps['countries/us/custom/us-small'], 'mapline'),
        color: 'silver',
        showInLegend: false,
        enableMouseTracking: false
      }]
    }
  }
  getOptions() {
    const { mapData, statesData } = this.prepareSeries()
    return this.getBaseOptions({ mapData, statesData })
  }
}


export class ElectionMapChartParty extends ElectionMapChartOverall {
  getColorValue ({ marketData, stateCode, stateName, stateVotes }) {
    if (!marketData.contracts) return 0;
    const targetContract = getLeadingTradedContract(marketData.contracts);
    if (!targetContract) return 0;
    const pallette = getColorForPalletteCandidate(targetContract.contractName);
    return roundToTwo(targetContract.lastTradePrice + pallette[0].from);
  }
  getColorAxisStops() {
    return [
      ...COLOR_MAP.GRAY,
      ...COLOR_MAP.BLUE,
      ...COLOR_MAP.RED,
    ];
  }
  getOptions() {
    const { mapData, statesData } = this.prepareSeries()
    const options = this.getBaseOptions({ mapData, statesData })
    return {
      ...options,
      title: {
        text: ''
      },
      colorAxis: {
        min: 0,
        dataClasses: this.getColorAxisStops()
      },
    }
  }
}

export class ElectionMapChartSenate extends ElectionMapChartOverall {
  getColorValue ({ marketData, stateCode, stateName, stateVotes }) {
    const hardcodedDemocrats = ['CA', 'CT', 'HI', 'IL', 'MD', 'NY', 'OR', 'VT', 'WA'];
    const hardcodedRepublicans = ['AK', 'AL', 'AR', 'IA', 'ID', 'IN', 'KS', 'KY', 'LA', 'MO', 'ND', 'OK', 'SC', 'SD', 'UT'];
    if (hardcodedDemocrats.includes((stateCode))) {
      return COLOR_MAP.BLUE[2].to;
    }
    if (hardcodedRepublicans.includes(stateCode)) {
      return COLOR_MAP.RED[2].to;
    }
    if (!marketData.contracts) return 0;
    const targetContract = getLeadingTradedContract(marketData.contracts);
    if (!targetContract) return 0;
    const pallette = getColorForPalletteCandidate(targetContract.contractName);
    return roundToTwo(targetContract.lastTradePrice + pallette[0].from);
  }
  formatDataLabel() {
    // if (this.point.stateCode === 'HI') return '';
    const contracts = this.point.marketData.contracts || [];
    const targetContract = getLeadingTradedContract(contracts);
    const stateCode = this.point.properties['hc-a2'];
    const showStates = [
      'VT', 'NH', 'MA', 'RI', 'CT', 'NJ', 'DE', 'MD', 'DC'
    ];
    const markWhenRepublicansAhead = [
      'DE', 'IL', 'MA', 'NH', 'NJ', 'OR',
      'RI', 'VA', 'MN', 'NM', 'MI', 'AL'
    ];
    const markWhenDemocratsAhead = [
      'AZ', 'CO', 'ME', 'MT', 'NC', 'GA', 'IA',
      'KS', 'AK', 'KY', 'SC', 'TX', 'AR', 'ID',
      'LA', 'MS', 'NE', 'OK', 'SD', 'TN', 'WV',
      'WY'
    ];
    if (!targetContract) {
      if (contracts.length > 0) {
        return '-';
      }
      return showStates.includes(stateCode) ? stateCode : '';
      // return stateCode;
    }
    const contract = new Contract(targetContract);

    const markPrice = (targetContract.contractName === 'Republican' && markWhenRepublicansAhead.includes(stateCode))
    || (targetContract.contractName === 'Democratic' && markWhenDemocratsAhead.includes(stateCode));
    if (markPrice) {
      return `<div>${contract.lastTradePriceAsCents}¢</div>`;
    }


    return `${contract.lastTradePriceAsCents}¢`;
  }
  formatTooltip() {
    const index = this.point.index;
    const data = this.point.series.userOptions.data[index];
    const { marketData } = data;
    if (!marketData.contracts) return false;
    const instance= new MarketStateTooltipComponent({
      propsData: {
        ...data,
        type: 'senate'
      }
    });
    instance.$mount();
    return instance.$el.outerHTML;
  }
  getColorAxisStops() {
    return [
      ...COLOR_MAP.GRAY,
      ...COLOR_MAP.BLUE,
      ...COLOR_MAP.RED,
    ];
  }
  getOptions() {
    const { mapData, statesData } = this.prepareSeries()
    const options = this.getBaseOptions({ mapData, statesData })
    return {
      ...options,
      title: {
        text: ''
      },
      colorAxis: {
        min: 0,
        dataClasses: this.getColorAxisStops()
      },
    }
  }
}



// WEBPACK FOOTER //
// ./src/utils/chart-models.js